<script setup>
// NOTICE: KAPTION-INTEGRATION — Horizontal funnel visualization for campaign metrics
// Shows: Sent → Delivered → Read → Replied with chevron connectors
// Failed shown as a separate ruby badge below
import { useI18n } from 'vue-i18n';

defineProps({
  stages: {
    type: Array,
    required: true,
    // Each: { key, label, value, percentage }
  },
  failed: {
    type: Object,
    default: null,
    // { value, percentage }
  },
});

const { t } = useI18n();

const STAGE_COLORS = [
  // slate → teal-7 → teal-9 → green-9
  {
    bg: 'bg-n-slate-3',
    border: 'border-n-slate-7',
    text: 'text-n-slate-12',
    badge: 'bg-n-slate-9 text-white',
  },
  {
    bg: 'bg-n-teal-3',
    border: 'border-n-teal-7',
    text: 'text-n-teal-12',
    badge: 'bg-n-teal-7 text-white',
  },
  {
    bg: 'bg-n-teal-3',
    border: 'border-n-teal-8',
    text: 'text-n-teal-12',
    badge: 'bg-n-teal-9 text-white',
  },
  {
    bg: 'bg-n-green-3',
    border: 'border-n-green-8',
    text: 'text-n-green-12',
    badge: 'bg-n-green-9 text-white',
  },
];
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Funnel row -->
    <div class="flex items-center gap-1">
      <template v-for="(stage, idx) in stages" :key="stage.key">
        <!-- Chevron connector -->
        <span
          v-if="idx > 0"
          class="i-lucide-chevron-right w-4 h-4 text-n-slate-8 shrink-0"
        />
        <!-- Stage pill -->
        <div
          class="flex items-center gap-2 px-3 py-2 rounded-lg border"
          :class="[
            STAGE_COLORS[idx]?.bg || 'bg-n-slate-3',
            STAGE_COLORS[idx]?.border || 'border-n-slate-7',
          ]"
        >
          <span
            class="text-sm font-medium"
            :class="STAGE_COLORS[idx]?.text || 'text-n-slate-12'"
          >
            {{ stage.label }}
          </span>
          <span
            class="px-1.5 py-0.5 text-xs font-bold rounded"
            :class="STAGE_COLORS[idx]?.badge || 'bg-n-slate-9 text-white'"
          >
            {{ stage.value.toLocaleString() }}
          </span>
          <span
            v-if="stage.percentage !== null && stage.percentage !== undefined"
            class="text-xs text-n-slate-10"
          >
            {{ stage.percentage }}%
          </span>
        </div>
      </template>
    </div>

    <!-- Failed badge -->
    <div v-if="failed && failed.value > 0" class="flex items-center gap-2">
      <span class="i-lucide-alert-triangle w-4 h-4 text-n-ruby-9" />
      <span class="text-sm text-n-ruby-11 font-medium">
        {{ failed.value.toLocaleString() }}
        {{ t('CAMPAIGN.REPORT.METRICS.FAILED').toLowerCase() }}
        <span v-if="failed.percentage" class="text-n-ruby-9">
          ({{ failed.percentage }}%)
        </span>
      </span>
    </div>
  </div>
</template>
